import { Alert, Box, Card, Pagination, Snackbar, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import UserListHeader from "./userListHeader";
import RenderUsersList from "./renderUsersList";
import { useDispatch, useSelector } from "react-redux";
import SearchField from "@common/components/SearchField/searchField";
import { useNavigate } from "react-router";
import { fetchUsers } from "@store/userListSlice";
import { clearLoadingState } from "@store/addUserSlice";
import { AppDispatch } from "@store/store";

const UserList: React.FC = () => {
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSecurityGroup, setSelectedSecurityGroup] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [updateSearchValue, setUpdateSearchValue] = useState("");
  const { user } = useSelector((state: any) => state.user);
  const { addUserloading, updateUserloading } = useSelector(
    (state: any) => state.addUser
  );
  const { users, securityGroups, organizations, totalCount, loading } = useSelector((state: any) => state.userList);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const statuses = ["Active", "Inactive"];

  useEffect(() => {
    const handler = setTimeout(() => {
      setUpdateSearchValue(searchValue);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue, updateSearchValue]);

  useEffect(() => {
    const params = {
      page,
      pageSize: 20,
      isActive: selectedStatus,
      hospitalId: user.hospitalId,
      search: updateSearchValue,
      sortBy: orderBy,
      sortOrder: order,
      securityGroupId: selectedSecurityGroup,
    };
    dispatch(fetchUsers(params));
  }, [page]);

  useEffect(() => {
    if (page > 1) {
      setPage(1);
    }
    const params = {
      page: 1,
      pageSize: 20,
      isActive: selectedStatus,
      hospitalId: user.hospitalId,
      search: updateSearchValue,
      sortBy: orderBy,
      sortOrder: order,
      securityGroupId: selectedSecurityGroup,
    };
    dispatch(fetchUsers(params));
  }, [
    selectedStatus,
    updateSearchValue,
    orderBy,
    order,
    selectedSecurityGroup,
  ]);

  const handleUserNavigation = (userId: number) => {
    navigate(`/users/${userId}`);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSecurityGroup = (
    event: React.ChangeEvent<HTMLInputElement | { value: string; name: string }>
  ) => {
    setSelectedSecurityGroup(event.target.value);
  };
  const handleStatus = (
    event: React.ChangeEvent<HTMLInputElement | { value: string; name: string }>
  ) => {
    setSelectedStatus(event.target.value);
  };

  const handleSearchUser = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (event?.target) {
      setSearchValue(event.target.value);
    }
  };

  const clearFilter = (value: string) => {
    if (value === "securityGroup") setSelectedSecurityGroup("");
    if (value === "status") setSelectedStatus("");
  };

  const handleAlertClose = () => {
    dispatch(clearLoadingState());
  };

  return (
    <>
      <Card>
        <Box component="div" className="pt-2 px-2 ">
          <Box className="flex flex-row justify-between border-dotted border-b-2 md:border-[#0000004D] border-[#666666]">
            <UserListHeader totalUserCount={totalCount} />
            <Box className="md:flex flex-row pb-2 gap-4 hidden">
              <SearchField
                style={{ padding: "2px 10px 2px 0px", fontSize: "14px" }}
                sx={{ marginLeft: 1, flex: 1, padding: 0 }}
                handleSearchTicket={handleSearchUser}
                placeHolder="Search User..."
                searchValue={searchValue}
                maxLength={50}
              />
            </Box>
          </Box>
        </Box>

        <Box className="flex flex-col py-4 px-3 gap-4  md:hidden">
          <SearchField
            style={{ padding: "2px 0 2px", fontSize: "14px" }}
            sx={{ marginLeft: 1, flex: 1, padding: 0 }}
            handleSearchTicket={handleSearchUser}
            placeHolder="Search User..."
            searchValue={searchValue}
            maxLength={50}
          />
        </Box>

        <Box className="grid grid-cols-1 divide-y pt-1">
          <RenderUsersList
            users={users}
            securityGroups={securityGroups}
            statuses={statuses}
            handleUserNavigation={handleUserNavigation}
            handleSort={handleSort}
            order={order}
            orderBy={orderBy}
            handleSecurityGroup={handleSecurityGroup}
            handleStatus={handleStatus}
            selectedSecurityGroup={selectedSecurityGroup}
            organizations={organizations}
            clearFilter={clearFilter}
            loading={loading}
          />
        </Box>
        <Stack spacing={2}>
          <Pagination
            className="flex justify-center py-4"
            sx={{
              "& .MuiPagination-ul": {
                gap: { sm: "0.5rem" },
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#fff !important", 
                color: "#318FE0",             
                border: "1px solid #318FE0",
              }
            }}
            size="small"
            count={Math.ceil(totalCount / 20)} //totalUsers/ rowsPerPage
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            shape="rounded"
          />
        </Stack>
      </Card>
      <Snackbar
        className="!pt-[8rem] md:!pt-14"
        open={!addUserloading || !updateUserloading}
        autoHideDuration={3000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert variant="filled" severity="success" icon={false}   sx={{
    bgcolor: !addUserloading ? '#008528' : "#318FE0",      // Background color (filled variant)
    color: '#fff',           // Text color (optional, make sure it's readable)
    '& .MuiAlert-icon': {
      color: '#fff',         // Icon color
    },
  }}>
          {!addUserloading && "Saved Successfully"}
          {!updateUserloading && "Updated Successfully"}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserList;
